import React, { FC, useCallback, useEffect, useMemo, useRef } from "react";
import _ from "lodash";
import { OrderedSet } from "immutable";
import { connect, Dispatch, MapDispatchToPropsParam } from "react-redux";
import { makeStyles } from "@material-ui/core";
import classnames from "classnames";
import "ag-grid-community/dist/styles/ag-grid.css";
import "ag-grid-community/dist/styles/ag-theme-alpine.css";
import { useFlags, withLDConsumer } from "launchdarkly-react-client-sdk";
import { Schema } from "types/schema";
import { ColDef, ColumnApi, GridApi, InitialGroupOrderComparatorParams } from "ag-grid-community";
import { theme } from "styles/theme";
import { IStore } from "reducers";
import type { IScope } from "reducers/scope";
import {
    buildGridItems,
    getLocationGroupSortComparator,
    IGridItem,
} from "helpers/legacy-cart/line-items/items-conversion-helper";
import { IProps as ProductCollectionGridViewProps, SizingViewGrid } from "./SizingViewGrid";
import { IGridContext } from "components/shipment-review/types";
import { CartGridView } from "reducers/cart-grid-view";
import cartGridViewActions from "actions/cart-grid-view";
import { CartTopBar } from "components/product-collection/product-collection-views/product-collection-grid-view/grid-renderers/pinned-row-renderers/cart-top-bar";
import { GridViewStoredState } from "helpers/porduct-collection-types";
import { GridViewCartTotals } from "components/shipment-review/grid-components/grid-view-cart-totals";
import { schemaToCollectionsSchema } from "helpers/legacy-cart/schema/schema-conversion-helper";
import { IOrderData, VIEW_MODE } from "types/cart/shipment-review";
import { IViewDefinition } from "types/view-definitions";
import { ILineItem } from "constants/order-types";
import { getLineItemsFDMFields, setDetailGridRowTotals } from "./helpers";
import { useDebounced } from "hooks/useDebounce";
import { COLUMN_FIELDS } from "constants/sizingView";
import useGridStateHandlers from "containers/legacy-cart/hooks/useGridStateHandlers";
import { ALLOWED_FILTER_KEYS, mergeColumns } from "./helpers/columnsHelper";
import { Notifications } from "containers/legacy-cart/grid-components/Notifications";
import { INotification } from "reducers/cart-notifications";
import { postToTopIframe } from "helpers/window-messages";
import { syncCartNotifications } from "actions/cart-legacy";
import { useBuildOrder } from "hooks/useBuildOrder";

const useStyles = makeStyles({
    gridViewCartWrap: {
        height: "100%",
        display: "flex",
        flexDirection: "column",
        "& .ag-popup .ag-tabs-header .ag-tab": {
            flex: "0 0 33.33%",
        },
        "& .ag-cell, & .totals": {
            alignItems: "center",
        },
        "& .totals": {
            marginTop: "4px",
        },
        "& .ag-column-panel": {
            fontFamily: "Lato, sans-serif",
            "& .ag-column-drop-empty-message": {
                lineHeight: "1.33",
                letterSpacing: "0.15px",
                color: theme.palette.secondaryTextColor,
            },
            "& .ag-column-drop-cell-text": {
                lineHeight: "1.33",
                letterSpacing: "0.15px",
                color: theme.palette.darkGrey,
            },
            "& .ag-column-drop-cell": {
                backgroundColor: "#ddd",
            },
        },
        "& .ag-column-panel, & .ag-filter-toolpanel": {
            "& .ag-icon-none::before, .ag-filter-toolpanel-search .ag-filter-toolpanel-expand .ag-icon-tree-open::before":
                {
                    content: '"\\f338"', // some issue in ag grid front with none icon, it should be empty but shows sort. Icon has been taken from here https://www.ag-grid.com/react-data-grid/custom-icons/#replace-the-icons-by-changing-the-icon-font
                },
        },
    },
});

interface IOwnProps {
    orderData: IOrderData;
    productSchema: Schema;
    orderSchema: Schema;
    itemView: IViewDefinition;
    isFetching: boolean;
}

interface IStateProps extends Partial<CartGridView> {
    scope: IScope;
    notifications: INotification[];
}

interface IDispatchProps extends Pick<ProductCollectionGridViewProps, "onBulkItemsSet" | "onRowExpandChange"> {
    setRowIds: typeof cartGridViewActions.setRowIds;
    setGridState: (state: GridViewStoredState) => void;
    setSearch: (term: string) => void;
    onChangeViewMode: (viewMode: VIEW_MODE) => void;
    onChangeActiveView: (activeView: string) => void;
    updateGridView: (updatedGridView: Partial<CartGridView>) => void;
    deselectAll: () => void;
}

export const GridViewContainer: FC<IOwnProps & IStateProps & IDispatchProps> = (props) => {
    const { orderData, itemView, productSchema, orderSchema } = props;
    const { scope, customViews, isFetching } = props;
    const { onChangeActiveView, updateGridView, setRowIds, setGridState, setSearch, deselectAll } = props;
    const { onChangeViewMode } = props;
    const { onBulkItemsSet, onRowExpandChange } = props;
    const { viewMode, activeView, rowIds, selectedRows, expandedSizes, gridState, searchText } = props;
    const { notifications } = props;
    const classes = useStyles();

    const { legacyCartEditableSizingGrid } = useFlags();
    const gridApi = useRef<GridApi>(null);
    const columnApi = useRef<ColumnApi>(null);

    const { onResetGrid, onSetGridState } = useGridStateHandlers({
        gridColumnApi: columnApi.current,
        gridApi: gridApi.current,
    });

    const items = useMemo<IGridItem[]>(
        () =>
            buildGridItems(
                orderData.entries,
                {
                    productSchema,
                    orderSchema,
                    shipments: orderData.shipments,
                    usePricingService: scope?.brand.isPricingPromotionEnabled(scope),
                    usePricePerSize: scope?.brand.hasPreference("price_per_size"),
                    currency: orderData.currency,
                    shipmentGrouping: viewMode === VIEW_MODE.SHIPMENT,
                },
                {
                    shipments: orderData.shipments,
                    availability: orderData.availability,
                    currency: orderData.currency,
                    deliveryConfig: orderData.deliveryConfig,
                },
            ),
        [
            orderData.entries,
            orderData.shipments,
            orderData.availability,
            orderData.currency,
            orderData.deliveryConfig,
            productSchema,
            orderSchema,
            scope,
            viewMode,
        ],
    );
    const columnDefs = useMemo<ColDef[]>(
        () =>
            schemaToCollectionsSchema(productSchema, itemView, {
                currency: orderData?.currency,
                useBulk: orderData?.useBulk,
                useShipmentColumn: viewMode === VIEW_MODE.SHIPMENT,
            }),
        [productSchema, itemView, orderData?.currency, orderData?.useBulk, viewMode],
    );

    const fdm = useMemo(() => getLineItemsFDMFields(orderSchema?.fields), [orderSchema]);

    const updateSearch = useCallback(
        (term: string) => {
            setSearch(term);
            deselectAll();
            gridApi.current.setQuickFilter(term);
        },
        [deselectAll, setSearch],
    );

    const toggleDeliveryGrouping = useCallback(() => {
        deselectAll();
        onChangeViewMode(viewMode === VIEW_MODE.SHIPMENT ? VIEW_MODE.INITIAL : VIEW_MODE.SHIPMENT);
    }, [deselectAll, onChangeViewMode, viewMode]);

    const clearFilters = useCallback(() => {
        updateSearch?.("");
        deselectAll();
        onResetGrid();
    }, [deselectAll, onResetGrid, updateSearch]);

    const showReset = useMemo(() => {
        const hasColumnChanges = () => {
            const grouping = _.filter(
                gridState?.columns,
                ({ rowGroup, colId }) => rowGroup && !_.includes(_.values(COLUMN_FIELDS), colId), // include delivery and location filter to default state
            );
            return (
                _.some(gridState?.columns, "sort") ||
                (!_.isEmpty(grouping) && !(_.size(grouping) === 1 && grouping[0].colId === COLUMN_FIELDS.LOCATION))
            );
        };
        return !!searchText || !_.isEmpty(gridState?.filterModel) || hasColumnChanges();
    }, [searchText, gridState?.columns, gridState?.filterModel]);

    // re-renders the grid on locations change
    const groupSortComparator = useCallback<(params: InitialGroupOrderComparatorParams) => number>(
        (params) =>
            getLocationGroupSortComparator(
                _.map(orderData?.selectedLocations, (location) => _.toUpper(location?.name)),
            )(params),
        [orderData?.selectedLocations],
    );

    useEffect(() => {
        postToTopIframe(syncCartNotifications());
    }, []);

    // on items change, synchronizes ids of checked and expanded items in the global state
    useEffect(() => {
        if (_.size(items) && !isFetching) {
            const selectedIds: string[] = [];
            const selectedItems: Array<Pick<ILineItem, "_id" | "product">> = [];
            _.forEach(items, ({ rowId, entries, productId }) => {
                selectedIds.push(rowId);
                selectedItems.push(
                    ..._.map(entries, ({ cartLineItemId }) => ({ _id: cartLineItemId, product: productId })),
                );
            });
            const ids = OrderedSet<string>(selectedIds);
            if (!ids.equals(rowIds)) {
                setRowIds(ids);

                if (legacyCartEditableSizingGrid) {
                    const added = ids.subtract(rowIds);

                    const expandedUpdated = expandedSizes.intersect(ids).merge(added);
                    if (!expandedUpdated.equals(expandedSizes)) {
                        onRowExpandChange(expandedUpdated, true, false);
                    }

                    const selectedUpdated = selectedRows.intersect(ids);
                    if (!selectedUpdated.equals(selectedRows)) {
                        onBulkItemsSet(selectedUpdated, selectedItems);
                    }
                }
            }
        }
    }, [
        items,
        isFetching,
        rowIds,
        expandedSizes,
        selectedRows,
        onBulkItemsSet,
        onRowExpandChange,
        setRowIds,
        legacyCartEditableSizingGrid,
    ]);

    const context = useRef<IGridContext>({
        itemView,
        brandId: scope.brand._id,
        fdm,
        isBuildOrderEnabled: useBuildOrder()?.isBuildOrderEnabled,
    });

    useEffect(() => {
        // Store within a mutable object since context does not force grid to rerender
        Object.assign(context.current, { itemView, brandId: scope.brand._id });
    }, [itemView, scope?.brand?._id]);

    const updateTotals = useCallback(() => {
        // TODO GV: the original approach that can slowdown performance for large number of detail grid
        gridApi.current?.forEachDetailGridInfo(({ api }) => setDetailGridRowTotals(api, orderData?.currency));
    }, [orderData?.currency]);
    const updateTotalDebounce = useDebounced(updateTotals, 200);

    useEffect(() => {
        // Recalculate pinned total data while there are no other triggers for pinned rows
        updateTotalDebounce();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [orderData?.entries]);

    useEffect(() => {
        if (gridApi?.current) {
            gridApi.current.setQuickFilter(searchText);
        }
    }, [gridApi?.current]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (_.isUndefined(activeView) && customViews && gridApi?.current && columnApi?.current) {
            const defaultView = _.find(customViews, { isDefault: true });
            if (defaultView) {
                const update = {
                    columns: mergeColumns(columnApi?.current?.getColumnState(), defaultView.columns),
                    filterModel: _.pick(defaultView.filters, ALLOWED_FILTER_KEYS),
                    toolpanel: null,
                };
                updateGridView({ gridState: update });
                onSetGridState(update);
            }
            onChangeActiveView(activeView || defaultView?._id || null);
        }
    }, [activeView, gridApi?.current && columnApi?.current]); // eslint-disable-line react-hooks/exhaustive-deps

    const onGridMount = useCallback<ProductCollectionGridViewProps["onGridMount"]>((gApi, cApi): void => {
        gridApi.current = gApi;
        columnApi.current = cApi;
    }, []);

    const handleStateChange = useCallback(
        (state: GridViewStoredState) => {
            setGridState(state);
            if (_.has(state, "filterModel") || (_.has(state, "pivot") && state.pivot?.mode !== gridState.pivot?.mode)) {
                deselectAll();
            }
        },
        [gridState, deselectAll, setGridState],
    );

    return (
        <div className={classnames("ag-theme-alpine", classes.gridViewCartWrap)} id="legacy-cart-grid-view">
            {!_.isEmpty(notifications) && <Notifications notifications={notifications} />}
            {legacyCartEditableSizingGrid && (
                <CartTopBar
                    viewMode={viewMode}
                    searchText={searchText}
                    onChangeDeliveryGrouping={toggleDeliveryGrouping}
                    onChangeSearch={updateSearch}
                    onClearFilterAndSearch={clearFilters}
                    showResetFilters={showReset}
                />
            )}
            <SizingViewGrid
                columnDefs={columnDefs}
                items={items}
                selectedRowIds={selectedRows}
                expandedRowIds={expandedSizes}
                onBulkItemsSet={legacyCartEditableSizingGrid ? onBulkItemsSet : undefined}
                onRowExpandChange={legacyCartEditableSizingGrid ? onRowExpandChange : undefined}
                groupSortComparator={groupSortComparator}
                onGridMount={onGridMount}
                storedState={gridState}
                onStoredStateChange={handleStateChange}
                context={context.current}
            />
            <GridViewCartTotals entries={orderData.entries} currency={orderData?.currency} gridApi={gridApi.current} />
        </div>
    );
};

const mapStateToProps = ({ cartGridView, cartNotifications, scope }: IStore): IStateProps => ({
    rowIds: cartGridView.rowIds,
    notifications: cartNotifications.active,
    viewMode: cartGridView.viewMode,
    activeView: cartGridView.activeView,
    selectedRows: cartGridView.selectedRows,
    expandedSizes: cartGridView.expandedSizes,
    gridState: cartGridView.gridState,
    searchText: cartGridView.searchText,
    customViews: cartGridView.customViews,
    scope,
});

const mapDispatchToProps: MapDispatchToPropsParam<IDispatchProps, IOwnProps> = (
    dispatch: Dispatch<any>,
): IDispatchProps => ({
    setRowIds: (ids) => dispatch(cartGridViewActions.setRowIds(ids, true)),
    onBulkItemsSet: (ids, selectedItems) => dispatch(cartGridViewActions.setSelected({ ids, selectedItems }, true)),
    onRowExpandChange: (ids) => dispatch(cartGridViewActions.setShownSizes(ids, true)),
    setGridState: (gridState) => dispatch(cartGridViewActions.updateState(gridState, true)),
    setSearch: (searchText) => dispatch(cartGridViewActions.update({ searchText }, true)),
    onChangeViewMode: (viewMode) => dispatch(cartGridViewActions.update({ viewMode }, true)),
    onChangeActiveView: (activeView) => dispatch(cartGridViewActions.update({ activeView }, true)),
    updateGridView: (updatedGridView) => dispatch(cartGridViewActions.update(updatedGridView)),
    deselectAll: () => dispatch(cartGridViewActions.deselectAll(true)),
});

export default withLDConsumer()(
    connect<IStateProps, IDispatchProps, IOwnProps>(mapStateToProps, mapDispatchToProps)(GridViewContainer),
);
